import React, { useRef, useEffect } from "react";
import * as d3 from "d3";
import { rccColors } from "../colors";
import { useRccTypography } from "../typography";

// Font family constant - matches typography system
const FONT_FAMILY = '"Onest", sans-serif';

/**
 * D3Linearity - Simple grouped bar chart comparing Quota vs Won (QTD + EOQ)
 * Fills available container space automatically.
 *
 * @param {Object} props
 * @param {Array} props.data - Chart data array with shape:
 *   [{ [xField]: string, quota: number, wonEOQ: number, wonQTD: number, isCurrentQtr?: boolean, qtrProjectionMid?: number, qtrProjectionHigh?: number }]
 * @param {string} props.xField - Field name to use for x-axis (default: "quarter")
 * @param {string} props.currencySymbol - Currency symbol to use instead of "$" (optional)
 */
function D3Linearity({
  data,
  xField = "quarter",
  currencySymbol = "",
  periodType = "quarter",
}) {
  const wrapperRef = useRef(null);
  const containerRef = useRef(null);
  const lastDimensions = useRef({ width: 0, height: 0 });
  const typography = useRccTypography();

  const colorPalette = rccColors.linearityPairedPalette;

  // Color palette
  const colors = {
    quota: colorPalette.quota.color,
    quotaText: colorPalette.quota.textColor,

    wonQTD: colorPalette.wonQTD.color,
    wonQTDText: colorPalette.wonQTD.textColor,

    wonEOQ: colorPalette.wonEOQ.color,
    wonEOQText: colorPalette.wonEOQ.textColor,

    // Static colors for axis/legend text (from general palette)
    axisText: rccColors.neutral[800], // Grey 800 for axis values
    axisLine: rccColors.neutral[100], // Grey 100 for axis lines
    legendText: rccColors.neutral[800], // Grey 800 for legend text

    // Projection colors (Green scale)
    projLow: rccColors.green[400],
    projMid: rccColors.green[200],
    projHigh: rccColors.green[50],
    projStroke: rccColors.lightGreen[900],
    projText: rccColors.neutral[800], // Dark grey for labels on top
  };

  const legendItems = [
    { key: "quota", label: "QUOTA", color: colors.quota },
    {
      key: "wonEOQ",
      label: periodType === "month" ? "WON EOM" : "WON EOQ",
      color: colors.wonEOQ,
    },
    {
      key: "wonQTD",
      label: periodType === "month" ? "WON MTD" : "WON QTD",
      color: colors.wonQTD,
    },
  ];

  const kFormatter = (v) => {
    if (v === 0) return "0";
    if (Math.abs(v) < 1000) return d3.format(",")(v);
    return d3.format(",.0f")(v / 1000) + "K";
  };

  const mFormatter = (v) => {
    if (v === 0) return "0";
    if (Math.abs(v) < 1000) return d3.format(",")(v);
    return d3.format(",.1f")(v / 1_000_000) + "M";
  };

  useEffect(() => {
    if (!wrapperRef.current || !containerRef.current || !data?.length) return;

    const renderChart = () => {
      const wrapper = wrapperRef.current;
      const container = containerRef.current;
      if (!wrapper || !container) return;

      // Get dimensions from wrapper (parent element with actual dimensions)
      const containerWidth = wrapper.clientWidth;
      const containerHeight = wrapper.clientHeight;

      const margin = { top: 25, right: 20, bottom: 50, left: 55 };
      const width = containerWidth - margin.left - margin.right;
      const chartHeight = containerHeight - margin.top - margin.bottom;

      // Skip if dimensions are invalid
      if (width <= 0 || chartHeight <= 0) return;

      // Skip if dimensions haven't changed (prevents resize loop)
      const { width: lastW, height: lastH } = lastDimensions.current;
      if (containerWidth === lastW && containerHeight === lastH) return;

      // Store current dimensions before drawing
      lastDimensions.current = {
        width: containerWidth,
        height: containerHeight,
      };

      // Clear previous SVG only when we're about to draw a new one
      d3.select(container).select("svg").remove();

      const svg = d3
        .select(container)
        .append("svg")
        .attr("width", containerWidth)
        .attr("height", containerHeight)
        .append("g")
        .attr("transform", `translate(${margin.left},${margin.top})`);

      // Scales
      const x0 = d3
        .scaleBand()
        .domain(data.map((d) => d[xField]))
        .range([0, width])
        .padding(0.3); // Padding between main groups

      // --- CUSTOM POSITIONING LOGIC ---
      // Instead of using a sub-scale (x1), we manually calculate positions
      // to ensure the bars stay together and centered.

      const MAX_BAR_WIDTH = 65; // Maximum width per bar
      const BAR_GAP = 8; // Fixed gap between Quota and Won bars

      const availableBandwidth = x0.bandwidth();

      // Calculate bar width: try to fit 2 bars + gap into available space
      // If space is huge, cap at MAX_BAR_WIDTH
      let barWidth = (availableBandwidth - BAR_GAP) / 2;
      if (barWidth > MAX_BAR_WIDTH) barWidth = MAX_BAR_WIDTH;

      // Ensure bar width isn't negative (edge case for very small screens)
      if (barWidth < 1) barWidth = 1;

      // Calculate the total width of the content (Bar + Gap + Bar)
      const groupContentWidth = barWidth * 2 + BAR_GAP;

      // Calculate the offset needed to center this content in the slot
      const centerOffset = (availableBandwidth - groupContentWidth) / 2;

      // Define relative offsets for the two bars
      const quotaOffset = centerOffset;
      const wonOffset = centerOffset + barWidth + BAR_GAP;
      // --------------------------------

      const maxVal = Math.max(
        d3.max(data, (d) => d.quota),
        d3.max(data, (d) => {
          const baseWon = (d.wonEOQ || 0) + (d.wonQTD || 0);

          // For current quarter, treat "Low" as the baseline projection total (if provided),
          // then stack Mid/High increments on top of that baseline.
          // This ensures y-scale accounts for qtrProjectionLow when present.
          if (d.isCurrentQtr) {
            const lowTotal = d.qtrProjectionLow || baseWon;
            const projMid = d.qtrProjectionMid || 0;
            const projHigh = d.qtrProjectionHigh || 0;
            return lowTotal + projMid + projHigh;
          }

          return baseWon;
        })
      );

      // If max value is >= 1 Million, use 'M' suffix, otherwise use 'K'
      const useMillions = maxVal >= 1_000_000;
      const activeFormatter = useMillions ? mFormatter : kFormatter;

      const y = d3
        .scaleLinear()
        .domain([0, maxVal * 1.4])
        .nice()
        .range([chartHeight, 0]);

      // Y-axis
      svg
        .append("g")
        .call(
          d3
            .axisLeft(y)
            .tickFormat((v) =>
              v === 0 ? "0" : `${currencySymbol}${activeFormatter(v)}`
            )
            .ticks(6)
        )
        .call((g) => g.select(".domain").attr("stroke", colors.axisLine))
        .call((g) => g.selectAll(".tick line").attr("stroke", colors.axisLine))
        .call((g) =>
          g
            .selectAll(".tick text")
            .style("font-family", FONT_FAMILY)
            .style("font-size", "11px")
            .style("fill", colors.axisText)
        );

      // X-axis
      svg
        .append("g")
        .attr("transform", `translate(0,${chartHeight})`)
        .call(d3.axisBottom(x0))
        .call((g) => g.select(".domain").attr("stroke", colors.axisLine))
        .call((g) => g.selectAll(".tick line").attr("stroke", colors.axisLine))
        .call((g) =>
          g
            .selectAll(".tick text")
            .style("font-family", FONT_FAMILY)
            .style("font-size", "11px")
            .style("fill", colors.axisText)
        );

      // Draw bars for each data point
      data.forEach((d) => {
        const groupX = x0(d[xField]); // Start position of the category slot
        const g = svg.append("g").attr("transform", `translate(${groupX},0)`);

        // Quota bar
        const qX = quotaOffset;
        const qW = barWidth;
        const qY = y(d.quota);
        const qH = chartHeight - qY;

        if (qH > 0) {
          g.append("rect")
            .attr("x", qX)
            .attr("y", qY)
            .attr("width", qW)
            .attr("height", qH)
            .attr("fill", colors.quota);
        }

        // Quota label on top (only if there's space above bar)
        if (qY > 20 && qW > 40) {
          g.append("text")
            .attr("x", qX + qW / 2)
            .attr("y", qY - 8)
            .attr("text-anchor", "middle")
            .attr("class", typography.textExtraSmallSemibold)
            .style("fill", colors.projText)
            .text(`${currencySymbol}${(d.quota / 1000000).toFixed(2)}M`);
        }

        // Won stacked bar (QTD bottom, EOQ top)
        const wX = wonOffset;
        const wW = barWidth;
        const totalWon = d.wonQTD + d.wonEOQ;

        // Calculate top of won bar for dashed rectangles (needed for current quarter)
        const topOfWonBar = d.wonEOQ > 0 ? y(d.wonQTD + d.wonEOQ) : y(d.wonQTD);

        if (totalWon > 0) {
          // WON QTD (bottom)
          const qtdY = y(d.wonQTD);
          const qtdH = chartHeight - qtdY;

          if (qtdH > 0) {
            g.append("rect")
              .attr("x", wX)
              .attr("y", qtdY)
              .attr("width", wW)
              .attr("height", qtdH)
              .attr("fill", colors.wonQTD);
          }

          // QTD label inside bar (only if bar is tall and wide enough)
          if (qtdH > 30 && wW > 35) {
            const pct =
              d.quota > 0 ? Math.round((d.wonQTD / d.quota) * 100) : 0;
            g.append("text")
              .attr("x", wX + wW / 2)
              .attr("y", qtdY + qtdH / 2 - 6)
              .attr("text-anchor", "middle")
              .style("font-family", FONT_FAMILY)
              .style("font-size", "10px")
              .style("font-weight", "500")
              .style("fill", colors.wonQTDText)
              .text(`${currencySymbol}${(d.wonQTD / 1000000).toFixed(2)}M`);

            g.append("text")
              .attr("x", wX + wW / 2)
              .attr("y", qtdY + qtdH / 2 + 8)
              .attr("text-anchor", "middle")
              .style("font-family", FONT_FAMILY)
              .style("font-size", "9px")
              .style("fill", colors.wonQTDText)
              .text(`(${pct}%)`);
          }

          // Total won label on top of QTD bar if no EOQ (only if there's space above)
          // Don't show for current quarter - projections will be shown instead
          if (d.wonEOQ === 0 && qtdY > 25 && wW > 40 && !d.isCurrentQtr) {
            const totalPct =
              d.quota > 0 ? Math.round((totalWon / d.quota) * 100) : 0;
            g.append("text")
              .attr("x", wX + wW / 2)
              .attr("y", qtdY - 18)
              .attr("text-anchor", "middle")
              .attr("class", typography.textExtraSmallSemibold)
              .style("fill", colors.projText)
              .text(`${currencySymbol}${(totalWon / 1000000).toFixed(2)}M`);

            g.append("text")
              .attr("x", wX + wW / 2)
              .attr("y", qtdY - 6)
              .attr("class", typography.textLabelSemibold)
              .style("fill", colors.projText)
              .text(`(${totalPct}%)`);
          }

          // WON EOQ (top)
          // For current quarter, EOQ is represented as part of the dotted "Low" projection segment instead
          // of a solid EOQ block.
          if (d.wonEOQ > 0 && !d.isCurrentQtr) {
            const eoqY = y(d.wonQTD + d.wonEOQ);
            const eoqH = y(d.wonQTD) - eoqY;

            if (eoqH > 0) {
              g.append("rect")
                .attr("x", wX)
                .attr("y", eoqY)
                .attr("width", wW)
                .attr("height", eoqH)
                .attr("fill", colors.wonEOQ);
            }

            // EOQ label inside bar (only if bar is tall and wide enough)
            // Don't show for current quarter - projections will be shown instead
            if (eoqH > 30 && wW > 35 && !d.isCurrentQtr) {
              const pct =
                d.quota > 0 ? Math.round((d.wonEOQ / d.quota) * 100) : 0;
              g.append("text")
                .attr("x", wX + wW / 2)
                .attr("y", eoqY + eoqH / 2 - 6)
                .attr("text-anchor", "middle")
                .style("font-family", FONT_FAMILY)
                .style("font-size", "10px")
                .style("font-weight", "500")
                .style("fill", colors.wonEOQText)
                .text(`${currencySymbol}${(d.wonEOQ / 1000000).toFixed(2)}M`);

              g.append("text")
                .attr("x", wX + wW / 2)
                .attr("y", eoqY + eoqH / 2 + 8)
                .attr("text-anchor", "middle")
                .style("font-family", FONT_FAMILY)
                .style("font-size", "9px")
                .style("fill", colors.wonEOQText)
                .text(`(${pct}%)`);
            }

            // Total won label on top of stacked bar (only if there's space above)
            // Don't show for current quarter - projections will be shown instead
            if (eoqY > 25 && wW > 40 && !d.isCurrentQtr) {
              const totalPct =
                d.quota > 0 ? Math.round((totalWon / d.quota) * 100) : 0;
              g.append("text")
                .attr("x", wX + wW / 2)
                .attr("y", eoqY - 18)
                .attr("text-anchor", "middle")
                .attr("class", typography.textExtraSmallSemibold)
                .style("fill", colors.projText)
                .text(`${currencySymbol}${(totalWon / 1000000).toFixed(2)}M`);

              g.append("text")
                .attr("x", wX + wW / 2)
                .attr("y", eoqY - 6)
                .attr("text-anchor", "middle")
                .attr("class", typography.textLabelSemibold)
                .style("fill", colors.projText)
                .text(`(${totalPct}%)`);
            }
          }
        }

        // DRAW PROJECTIONS (Even if totalWon is 0)
        // Projection rectangles for current quarter:
        // show QTD as the solid bar, then draw dotted Low/Mid/High segments above it.
        if (d.isCurrentQtr) {
          // Base is the top of the solid QTD bar (actuals-to-date)
          const baseTotal = d.wonQTD || 0;
          const baseY = y(baseTotal);

          // Defend against missing data by defaulting to 0
          const projMid = d.qtrProjectionMid || 0;
          const projHigh = d.qtrProjectionHigh || 0;

          // Low baseline projection total (fallback to current totalWon)
          const lowTotalRaw = d.qtrProjectionLow || totalWon;
          const lowTotal = Math.max(lowTotalRaw, baseTotal);

          // Mid/High totals are increments on top of Low
          const midTotal = Math.max(lowTotal + projMid, lowTotal);
          const highTotal = Math.max(lowTotal + projMid + projHigh, midTotal);

          // Low segment (closest to the bar)
          const lowY = y(lowTotal);
          const lowH = baseY - lowY;
          if (lowH > 0) {
            g.append("rect")
              .attr("x", wX)
              .attr("y", lowY)
              .attr("width", wW)
              .attr("height", lowH)
              .attr("fill", colors.projLow)
              .attr("stroke", colors.projStroke)
              .attr("stroke-width", 1.5)
              .attr("stroke-dasharray", "4,4");
          }

          // Mid segment
          const midY = y(midTotal);
          const midH = lowY - midY;
          if (midH > 0) {
            g.append("rect")
              .attr("x", wX)
              .attr("y", midY)
              .attr("width", wW)
              .attr("height", midH)
              .attr("fill", colors.projMid)
              .attr("stroke", colors.projStroke)
              .attr("stroke-width", 1.5)
              .attr("stroke-dasharray", "4,4");
          }

          // High segment (furthest from the bar)
          const highY = y(highTotal);
          const highH = midY - highY;
          if (highH > 0) {
            g.append("rect")
              .attr("x", wX)
              .attr("y", highY)
              .attr("width", wW)
              .attr("height", highH)
              .attr("fill", colors.projHigh)
              .attr("stroke", colors.projStroke)
              .attr("stroke-width", 1.5)
              .attr("stroke-dasharray", "4,4");
          }

          // Add projection labels (H, M, L) on top of the dotted bars
          // Note: These labels are appended after the dotted bars in the DOM,
          // so they will render on top visually
          const projectionHighTotal = highTotal;
          const projectionMidTotal = midTotal;
          const projectionLowTotal = lowTotal;

          // Position labels on top of the bar, starting at the same x position as the bar
          // wX is the left edge of the won bar, so labels should start exactly there
          const labelX = wX; // Start at the same x position as the bar (left edge)
          const labelOffset = 10; // Base vertical offset above each projection level (in pixels)
          const minLabelSpacing = 14; // Minimum spacing between labels to prevent overlap

          // Only show labels if there's enough space and at least some projection data exists
          if (highY > -50 && wW > 25) {
            // Use the original positioning logic that worked perfectly
            // Position labels relative to their projection levels (original working logic)
            let highLabelY = highY - labelOffset;
            let midLabelY = midY - labelOffset;
            // Keep "L" anchored just above the solid bar (same behavior as before)
            // so it doesn't drift into the bar when spacing adjustments kick in.
            let lowLabelY = baseY - labelOffset;

            // Ensure minimum spacing between labels to prevent overlap (original behavior)
            // Note: This keeps the label stack tight and avoids pushing labels into the bar.
            if (midLabelY > highLabelY - minLabelSpacing) {
              midLabelY = highLabelY - minLabelSpacing;
            }
            if (lowLabelY > midLabelY - minLabelSpacing) {
              lowLabelY = midLabelY - minLabelSpacing;
            }

            // Restore original visual ordering/placement:
            // L at the top, M in the middle, H closest to the bar.
            //
            // NOTE: This intentionally swaps which total is associated with H vs L,
            // matching the original implementation's behavior the team expects visually.

            const highPct =
              d.quota > 0
                ? Math.round((projectionHighTotal / d.quota) * 100)
                : 0;
            g.append("text")
              .attr("x", labelX)
              .attr("y", lowLabelY)
              .attr("text-anchor", "start")
              .attr("alignment-baseline", "baseline")
              .attr("class", typography.textExtraSmallSemibold)
              .style("fill", colors.projText)
              .text(
                `H: ${currencySymbol}${(projectionHighTotal / 1000000).toFixed(
                  2
                )}M (${highPct}%)`
              );

            // Mid label - positioned relative to mid projection level
            const midPct =
              d.quota > 0
                ? Math.round((projectionMidTotal / d.quota) * 100)
                : 0;
            g.append("text")
              .attr("x", labelX)
              .attr("y", midLabelY)
              .attr("text-anchor", "start")
              .attr("alignment-baseline", "baseline")
              .attr("class", typography.textExtraSmallSemibold)
              .style("fill", colors.projText)
              .text(
                `M: ${currencySymbol}${(projectionMidTotal / 1000000).toFixed(
                  2
                )}M (${midPct}%)`
              );

            const lowPct =
              d.quota > 0
                ? Math.round((projectionLowTotal / d.quota) * 100)
                : 0;
            g.append("text")
              .attr("x", labelX)
              .attr("y", highLabelY)
              .attr("text-anchor", "start")
              .attr("alignment-baseline", "baseline")
              .attr("class", typography.textExtraSmallSemibold)
              .style("fill", colors.projText)
              .text(
                `L: ${currencySymbol}${(projectionLowTotal / 1000000).toFixed(
                  2
                )}M (${lowPct}%)`
              );
          }
        }
      });
    };

    renderChart();

    // Debounced resize handler to avoid excessive redraws
    let resizeTimeout;
    const handleResize = () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(renderChart, 50);
    };

    const observer = new ResizeObserver(handleResize);
    observer.observe(wrapperRef.current);

    return () => {
      clearTimeout(resizeTimeout);
      observer.disconnect();
    };
  }, [data, xField, currencySymbol]);

  // Wrapper provides dimensions, inner div is absolutely positioned for SVG
  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <div
        ref={wrapperRef}
        style={{
          width: "100%",
          flex: 1,
          position: "relative",
        }}
      >
        <div
          ref={containerRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            overflow: "hidden",
          }}
        />
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          alignContent: "flex-start",
          gap: "8px 48px",
          alignSelf: "stretch",
          flexWrap: "wrap",
          paddingTop: "16px",
        }}
      >
        {legendItems.map((item) => (
          <div
            key={item.key}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                width: "6px",
                height: "6px",
                borderRadius: "50%",
                backgroundColor: item.color,
                flexShrink: 0,
              }}
            />
            <span
              className={typography.textSmallRegular}
              style={{ color: colors.legendText }}
            >
              {item.label}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default D3Linearity;
